// src/App.tsx
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth'; // Import from your consolidated hook
import LoadingScreen from './components/common/LoadingScreen';
import AdminRoute from './components/common/AdminRoute'; // Import the new admin route component
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from './contexts/ToastContext';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Lazy load components to improve initial load time
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const LogoutHandler = lazy(() => import('./pages/LogoutHandler'));
const CallbackHandler = lazy(() => import('./pages/CallbackHandler'));
const Admin = lazy(() => import('./pages/Admin')); // Import the new Admin page
const ProductsBrowser = lazy(() => import('./pages/ProductsBrowser')); // Import the Products Browser page
const PenniedItems = lazy(() => import('./pages/PenniedItems')); // Import the Pennied Items page
const CouponsBrowserWithStacks = lazy(() => import('./pages/CouponsBrowserWithStacks')); // Import the Coupons Browser with Stacks page
const CouponDetailPage = lazy(() => import('./pages/CouponDetailPage')); // Import the Coupon Detail page
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage')); // Import the Product Detail page
const DealSimulator = lazy(() => import('./pages/DealSimulator')); // Import the Deal Simulator page
const SimulationDetails = lazy(() => import('./pages/SimulationDetails')); // Import the Simulation Details page
const SimulationDeals = lazy(() => import('./pages/SimulationDeals')); // Import the Simulation Deals page
const SimulationDealDetail = lazy(() => import('./pages/SimulationDealDetail')); // Import the Simulation Deal Detail page
const ShowcaseDeals = lazy(() => import('./pages/ShowcaseDeals')); // Import the Showcase Deals page

// Define route configuration with access control
const routes = [
  {
    path: '/',
    element: <Navigate to="/dashboard" />,
    isProtected: false,
  },
  {
    path: '/dashboard',
    element: <Dashboard />,
    isProtected: true,
  },
  {
    path: '/profile',
    element: <Profile />,
    isProtected: true,
  },
  {
    path: '/products',
    element: <ProductsBrowser />,
    isProtected: true,
  },
  {
    path: '/products/:upc',
    element: <ProductDetailPage />,
    isProtected: true,
  },
  {
    path: '/pennied-items',
    element: <PenniedItems />,
    isProtected: true,
  },
  {
    path: '/coupons',
    element: <CouponsBrowserWithStacks />,
    isProtected: true,
  },
  {
    path: '/coupon/:id',
    element: <CouponDetailPage />,
    isProtected: true,
  },
  {
    path: '/deal-simulator',
    element: <DealSimulator />,
    isProtected: true,
  },
  {
    path: '/simulations/:jobId',
    element: <SimulationDetails />,
    isProtected: true,
  },
  {
    path: '/simulations/:jobId/deals',
    element: <SimulationDeals />,
    isProtected: true,
  },
  {
    path: '/simulations/:jobId/deals/:dealId',
    element: <SimulationDealDetail />,
    isProtected: true,
  },
  {
    path: '/showcase',
    element: <ShowcaseDeals />,
    isProtected: true,
  },
  {
    path: '/admin',
    element: <Admin />,
    isAdmin: true, // Mark this as an admin route
    isProtected: true,
  },
  {
    path: '/unauthorized',
    element: <Unauthorized />,
    isProtected: false,
  },
  {
    path: '/callback',
    element: <CallbackHandler />, // Use our dedicated callback handler
    isProtected: false,
  },
  // New dedicated logout route
  {
    path: '/logout',
    element: <LogoutHandler />,
    isProtected: false,
  },
  // 404 route
  {
    path: '*',
    element: <Navigate to="/dashboard" />,
    isProtected: false,
  }
];

const ProtectedRoute: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const auth = useAuth(); // Use your consolidated hook
  
  if (auth.isLoading) {
    return <LoadingScreen message="Checking authentication..." />;
  }
  
  return auth.isAuthenticated ? (
    <>{element}</>
  ) : (
    <Navigate to="/unauthorized" replace />
  );
};

const App: React.FC = () => {
  const auth = useAuth(); // Use your consolidated hook

  if (auth.isLoading) {
    return <LoadingScreen message="Loading application..." />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Router>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {routes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    route.isAdmin ? (
                      <AdminRoute element={route.element} />
                    ) : route.isProtected ? (
                      <ProtectedRoute element={route.element} />
                    ) : (
                      route.element
                    )
                  }
                />
              ))}
            </Routes>
          </Suspense>
        </Router>
      </ToastProvider>
    </QueryClientProvider>
  );
};

export default App;